import { useCallback, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography } from '@altermaind/web/ui';
import { Dialog } from '@/_shared/components/Dialog';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { getTimeElapsedSince } from '@/_shared/helpers/convertDateAndTimeToFormatForBackend';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getSurveysCampaignByIdReminderEmployeesCall,
  postSurveysCampaignByIdReminderCall,
} from '../../state';
import { TableNotInvitedEmployees } from './TableNotInvitedEmployees';

type Props = {
  currentCampaignId?: string;
  isOpen: boolean;
  closeDialog: () => void;
  total?: number;
  completed?: number;
  pending?: number;
  lastSendDate?: string;
  assessmentName?: string;
};

export const ReminderDialog = ({
  isOpen,
  closeDialog,
  total,
  completed,
  pending,
  currentCampaignId,
  lastSendDate,
  assessmentName,
}: Props) => {
  const t = useTranslations('surveys.campaign.reminderDialog');
  const sharedT = useTranslations();

  const { notify } = useNotification();
  const { genericResponsiveValue } = useResponsiveStyle();
  const [showEmployees, setShowEmployees] = useState(false);

  const {
    execute: executePostSurveysCampaignByIdReminder,
    loading: loadingPostSurveysCampaignByIdReminder,
    error: errorPostSurveysCampaignByIdReminder,
  } = postSurveysCampaignByIdReminderCall.hook();

  const {
    execute: executeGetSurveysCampaignByIdReminderEmployees,
    response: responseGetSurveysCampaignByIdReminderEmployees,
    loading: loadingGetSurveysCampaignByIdReminderEmployees,
  } = getSurveysCampaignByIdReminderEmployeesCall.hook();

  const handleCloseDialog = useCallback(() => {
    setShowEmployees(false);
    closeDialog();
  }, [closeDialog]);

  const sendReminder = useCallback(() => {
    if (currentCampaignId) {
      executePostSurveysCampaignByIdReminder({
        id: currentCampaignId,
        successSaga: () => {
          notify(sharedT('success.genericApiSuccess'), 'success');
          handleCloseDialog();
        },
        errorSaga: () => {
          notify(sharedT('errors.genericApiError'), 'error');
        },
      });
    }
  }, [
    currentCampaignId,
    executePostSurveysCampaignByIdReminder,
    notify,
    sharedT,
    handleCloseDialog,
  ]);

  const handleViewPeople = useCallback(() => {
    if (currentCampaignId) {
      executeGetSurveysCampaignByIdReminderEmployees({
        id: currentCampaignId,
        successSaga: () => {
          setShowEmployees(true);
        },
      });
    }
  }, [currentCampaignId, executeGetSurveysCampaignByIdReminderEmployees]);

  if (total === completed) {
    return null;
  }

  return (
    <Dialog
      maxWidth={genericResponsiveValue({
        breakPoints: { desktop: '900px' },
        defaultValue: '90vw',
      })}
      name='reminderSurvey'
      open={isOpen}
      title={undefined}
      onClose={handleCloseDialog}
      closeButton
    >
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>

      <Box mb='xs' mt='m' width='100%'>
        <Typography variant='p2/light' mb='xs' color='primary/text' textAlign='center'>
          {t(`description.${assessmentName}`, { pending })}
        </Typography>

        <Typography variant='p2/light' mb='s' color='primary/text' textAlign='center'>
          {t('confirmMessage')}
        </Typography>

        {!showEmployees ? (
          <>
            {/* eslint-disable-next-line @altermaind/no-magic-numbers */}
            {lastSendDate && getTimeElapsedSince(lastSendDate, 'hours') < 48 ? (
              <Typography variant='p2/light' mb='xs' color='primary/text' textAlign='center'>
                <RenderLocalesWithComponents
                  text={t('lastSendWarning')}
                  placeholders={{
                    '!{BOLD}!': (
                      <Typography variant='p2/bold' as='span'>
                        {t('lastSendWarningBold')}
                      </Typography>
                    ),
                  }}
                />
              </Typography>
            ) : null}
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              backgroundColor='background'
              borderRadius='m'
              paddingY='xxxs'
              paddingX='xs'
              width='100%'
              mt='s'
            >
              <Typography variant='p2/light' color='secondary'>
                {t('tabs.summary')}
              </Typography>
              <Button
                variant='link/primary'
                color='secondary'
                onClick={handleViewPeople}
                loading={loadingGetSurveysCampaignByIdReminderEmployees}
                style={{ textDecoration: 'underline' }}
              >
                {t('tabs.viewPeople')}
              </Button>
            </Box>
          </>
        ) : (
          <Box
            maxHeight={genericResponsiveValue({
              breakPoints: { desktop: '45vh', tablet: '40vh' },
              defaultValue: '50vh',
            })}
            overflowY='auto'
          >
            {responseGetSurveysCampaignByIdReminderEmployees?.departments?.map(department => (
              <Card
                key={department.name}
                mb='xs'
                padding='s'
                backgroundColor='grey/dark/background'
                borderRadius='m'
              >
                <Box
                  width='100%'
                  minWidth={0}
                  minHeight={0}
                  overflow={genericResponsiveValue({
                    breakPoints: { desktop: 'visible' },
                    defaultValue: 'auto',
                  })}
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  <Box
                    alignItems='center'
                    mb='s'
                    display='flex'
                    flexDirection='row'
                    justifyContent='flex-end'
                    width='100%'
                  >
                    <Box
                      display='flex'
                      width={genericResponsiveValue({
                        breakPoints: { desktop: 'auto' },
                        defaultValue: '100%',
                      })}
                      flexDirection={genericResponsiveValue({
                        breakPoints: { desktop: 'row' },
                        defaultValue: 'column',
                      })}
                      alignItems={genericResponsiveValue({
                        breakPoints: { desktop: 'center' },
                        defaultValue: 'flex-start',
                      })}
                      mt={genericResponsiveValue({
                        breakPoints: { desktop: 'xs' },
                        defaultValue: 's',
                      })}
                      flex={genericResponsiveValue({
                        breakPoints: { desktop: '1' },
                        defaultValue: undefined,
                      })}
                      justifyContent={genericResponsiveValue({
                        breakPoints: { desktop: 'space-between' },
                        defaultValue: undefined,
                      })}
                      px='xs'
                    >
                      <Typography variant='p1/bold' color='primary/text'>
                        {department.name?.toLowerCase() === 'unknown'
                          ? t('unknownDepartment')
                          : department.name}
                      </Typography>
                      <Box
                        ml={genericResponsiveValue({
                          breakPoints: { desktop: 'xl' },
                          defaultValue: 'none',
                        })}
                        alignItems='center'
                        flexDirection='row'
                        display='flex'
                        mt={genericResponsiveValue({
                          breakPoints: { desktop: 'none' },
                          defaultValue: 'xxs',
                        })}
                      >
                        <Svg name='users' size='s' />
                        <Typography ml='xxs' variant='h1'>
                          {department.employees?.length}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <TableNotInvitedEmployees employees={department.employees || []} />
                </Box>
              </Card>
            ))}
          </Box>
        )}

        <Box mt='s' alignItems='center' display='flex' flexDirection='row' justifyContent='center'>
          <Button variant='link/primary' onClick={handleCloseDialog} mr='s' mt='s'>
            {t('buttons.cancel')}
          </Button>
          <Button
            mt='s'
            spinnerColor='white'
            data-testid='button-send-reminder-survey'
            onClick={sendReminder}
            loading={loadingPostSurveysCampaignByIdReminder}
            disabled={
              loadingPostSurveysCampaignByIdReminder || !!errorPostSurveysCampaignByIdReminder
            }
          >
            {t('buttons.send')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
